import * as React from "react";
import { Separator } from "@/components/ui/separator";
import { ExternalLink } from "lucide-react";

interface Provider {
  id: string;
  key: string;
  name: string;
}

interface AiModel {
  id: string;
  name: string;
  endpointId: string;
  description?: string;
  originalTitle?: string;
  originalDescription?: string;
  providerId: string;
  createdAt: string;
  updatedAt: string;
  lastSyncedAt?: string;
  pricing?: {
    originalPrice?: number;
    currency?: string;
    unit?: string;
  };
}

interface SummaryTabProps {
  model: AiModel;
  providers: Provider[];
}

export function SummaryTab({ model, providers }: SummaryTabProps) {
  const provider = providers.find(p => p.id === model.providerId);

  // Generate provider model URL based on provider type
  const getProviderModelUrl = () => {
    if (!provider) return null;

    const endpointId = model.endpointId;

    switch (provider.key) {
      case 'fal':
        // fal.ai format: https://fal.ai/models/{canonical-name}
        return `https://fal.ai/models/${endpointId}`;
      default:
        return null;
    }
  };

  const providerUrl = getProviderModelUrl();

  return (
    <div className="space-y-3 mt-3">
      <div className="rounded-lg border p-4">
        <h3 className="text-sm font-semibold mb-3">Model Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Original Title</p>
            <p className="text-xs break-words">{model.originalTitle || model.name}</p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Model ID</p>
            <p className="text-xs font-mono break-all">{model.id}</p>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs text-muted-foreground mb-0.5">Original Description</p>
            <p className="text-xs break-words">{model.originalDescription || model.description || "N/A"}</p>
          </div>

          <Separator className="md:col-span-2" />

          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Endpoint ID</p>
            <p className="text-xs font-mono">{model.endpointId}</p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Provider</p>
            {providerUrl ? (
              <a
                href={providerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
              >
                {provider?.name || model.providerId}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <p className="text-xs">
                {provider?.name || model.providerId}
              </p>
            )}
          </div>

          {model.pricing && (
            <>
              <Separator className="md:col-span-2" />
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Pricing</p>
                <p className="text-xs">
                  {model.pricing.originalPrice?.toFixed(6)} {model.pricing.currency || "USD"} / {model.pricing.unit}
                </p>
              </div>
            </>
          )}

          <Separator className="md:col-span-2" />

          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Created At</p>
            <p className="text-xs">{new Date(model.createdAt).toLocaleString()}</p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Updated At</p>
            <p className="text-xs">{new Date(model.updatedAt).toLocaleString()}</p>
          </div>

          {model.lastSyncedAt && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Last Synced</p>
              <p className="text-xs">{new Date(model.lastSyncedAt).toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
